import {
  formatCurrency,
  formatDate,
  PAYMENT_METHOD_LABELS,
  type PaymentMethodValue,
  toDateInput,
} from '@crm/shared';
import { Badge, Card, CardContent, cn, Select, Spinner } from '@crm/ui';
import { AlertTriangle, CalendarClock, CircleDollarSign, TrendingUp, Wallet } from 'lucide-react';
import { useState } from 'react';
import {
  Avatar,
  ExportPdfButton,
  PageHeader,
  Pagination,
  PrintButton,
  StatCard,
} from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useSession } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { useCashFlow, useMachines, useReceivables } from '../api';

/** As faixas que o salao pergunta o tempo todo: quando cai o dinheiro. */
type TabKey = 'today' | 'tomorrow' | 'next' | 'overdue';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'today', label: 'Hoje' },
  { key: 'tomorrow', label: 'Amanha' },
  { key: 'next', label: 'Proximos' },
  { key: 'overdue', label: 'Atrasados' },
];

/** O filtro de data que cada aba manda para a API. */
function tabQuery(tab: TabKey) {
  const today = new Date();
  const day = (offset: number) =>
    new Date(today.getFullYear(), today.getMonth(), today.getDate() + offset);

  if (tab === 'overdue') return { status: 'overdue' as const };

  if (tab === 'today') {
    return {
      status: 'PENDING' as const,
      from: `${toDateInput(day(0))}T00:00:00`,
      to: `${toDateInput(day(0))}T23:59:59`,
    };
  }

  if (tab === 'tomorrow') {
    return {
      status: 'PENDING' as const,
      from: `${toDateInput(day(1))}T00:00:00`,
      to: `${toDateInput(day(1))}T23:59:59`,
    };
  }

  return {
    status: 'PENDING' as const,
    from: `${toDateInput(day(2))}T00:00:00`,
    to: `${toDateInput(day(60))}T23:59:59`,
  };
}

const METHOD_TONE: Record<PaymentMethodValue, string> = {
  CASH: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  PIX: 'bg-teal-500/15 text-teal-700 dark:text-teal-400',
  DEBIT: 'bg-blue-500/15 text-blue-700 dark:text-blue-400',
  CREDIT: 'bg-violet-500/15 text-violet-700 dark:text-violet-400',
};

/** Fatias da previsao em barras: a maior enche a linha e serve de escala. */
function BreakdownCard({
  title,
  rows,
}: {
  title: string;
  rows: { label: string; amount: number }[];
}) {
  const top = Math.max(...rows.map((row) => row.amount), 0);

  return (
    <Card variant="flush">
      <CardContent className="space-y-3">
        <p className="text-sm font-medium">{title}</p>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sem previsao.</p>
        ) : (
          <ul className="space-y-2.5">
            {rows.map((row) => (
              <li key={row.label} className="space-y-1">
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate">{row.label}</span>
                  <span className="shrink-0 whitespace-nowrap font-medium">
                    {formatCurrency(row.amount)}
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${top > 0 ? (row.amount / top) * 100 : 0}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Previsao de recebimentos: o que ja foi vendido e quando cai na conta, com a
 * taxa da maquininha ja descontada. Aqui nao se da baixa - a tela responde
 * "quanto entra e quando", nao "ja entrou".
 */
export function ReceivablesPage() {
  useDocumentTitle('Recebimentos');

  const { user } = useSession();
  const units = user?.units ?? [];

  const [tab, setTab] = useState<TabKey>('today');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [method, setMethod] = useState('');
  const [machineId, setMachineId] = useState('');
  const [unitId, setUnitId] = useState('');

  const machines = useMachines();
  const cashFlow = useCashFlow(unitId || undefined);
  const receivables = useReceivables({
    page,
    perPage,
    ...tabQuery(tab),
    method: (method || undefined) as PaymentMethodValue | undefined,
    machineId: machineId || undefined,
    unitId: unitId || undefined,
  });

  const flow = cashFlow.data;
  const items = receivables.data?.data ?? [];

  // Alerta de concentracao: uma maquininha sozinha respondendo por quase tudo
  // e um risco que so aparece quando alguem soma - entao a tela soma.
  const topMachine = flow?.byMachine[0];
  const share =
    topMachine && flow && flow.pendingTotal > 0
      ? Math.round((topMachine.amount / flow.pendingTotal) * 100)
      : 0;
  const concentration =
    topMachine && share >= 60 && flow && flow.byMachine.length > 1
      ? { label: topMachine.label, share }
      : null;

  function changeTab(next: TabKey) {
    setTab(next);
    setPage(1);
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={<CircleDollarSign />}
        title="Recebimentos"
        description="O que ja foi vendido e ainda vai cair na conta, com a taxa descontada."
        actions={
          <div className="flex items-center gap-2">
            <PrintButton />
            <ExportPdfButton title="Recebimentos no cartao" />
          </div>
        }
      />

      {/* A previsao em quatro numeros grandes: e a pergunta do dia. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          title="Cai hoje"
          value={formatCurrency(flow?.today ?? 0)}
          hint={`Amanha ${formatCurrency(flow?.tomorrow ?? 0)}`}
          icon={<Wallet />}
        />
        <StatCard
          title="Proximos 7 dias"
          value={formatCurrency(flow?.nextSevenDays ?? 0)}
          hint={`30 dias ${formatCurrency(flow?.nextThirtyDays ?? 0)}`}
          icon={<CalendarClock />}
        />
        <StatCard
          title="Total previsto"
          value={formatCurrency(flow?.pendingTotal ?? 0)}
          hint="Tudo que ainda vai cair"
          icon={<TrendingUp />}
        />
        <StatCard
          title="Atrasado"
          value={formatCurrency(flow?.overdue ?? 0)}
          hint="Deveria ter caido e nao caiu"
          icon={<AlertTriangle />}
          className={flow && flow.overdue > 0 ? 'border-destructive/40' : undefined}
        />
      </div>

      {flow && flow.overdue > 0 ? (
        <button
          type="button"
          onClick={() => changeTab('overdue')}
          className="flex w-full items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-left text-sm text-destructive"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {formatCurrency(flow.overdue)} deveriam ter caido e ainda nao cairam. Toque para ver.
        </button>
      ) : null}

      {concentration ? (
        <p className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          {concentration.share}% do previsto vem de {concentration.label}. Se ela atrasar, atrasa
          quase tudo.
        </p>
      ) : null}

      {/* De onde vem a previsao: forma, maquininha e quem atendeu. */}
      {flow && flow.pendingTotal > 0 ? (
        <div className="grid gap-3 md:grid-cols-3">
          <BreakdownCard
            title="Por forma"
            rows={flow.byMethod.map((row) => ({
              label: PAYMENT_METHOD_LABELS[row.method],
              amount: row.amount,
            }))}
          />
          <BreakdownCard title="Por maquininha" rows={flow.byMachine} />
          <BreakdownCard title="Por profissional" rows={flow.byProfessional} />
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Select
          aria-label="Forma de pagamento"
          value={method}
          onChange={(event) => {
            setMethod(event.target.value);
            setPage(1);
          }}
        >
          <option value="">Todas as formas</option>
          {(Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethodValue[]).map((value) => (
            <option key={value} value={value}>
              {PAYMENT_METHOD_LABELS[value]}
            </option>
          ))}
        </Select>

        <Select
          aria-label="Maquininha"
          value={machineId}
          onChange={(event) => {
            setMachineId(event.target.value);
            setPage(1);
          }}
        >
          {/* Rotulo curto: "Todas as maquininhas" nao cabe em meia tela de
              celular e encostava na seta do seletor. */}
          <option value="">Maquininhas</option>
          {(machines.data?.data ?? []).map((machine) => (
            <option key={machine.id} value={machine.id}>
              {machine.label}
            </option>
          ))}
        </Select>

        {units.length > 1 ? (
          <Select
            className="col-span-2 sm:col-span-1"
            aria-label="Unidade"
            value={unitId}
            onChange={(event) => {
              setUnitId(event.target.value);
              setPage(1);
            }}
          >
            <option value="">Todas as unidades</option>
            {units.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.name}
              </option>
            ))}
          </Select>
        ) : null}
      </div>

      <div className="no-scrollbar -mx-1 flex gap-1 overflow-x-auto px-1 pb-1">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            onClick={() => changeTab(entry.key)}
            className={cn(
              'shrink-0 rounded-full border px-4 py-2 text-sm transition-colors',
              tab === entry.key
                ? 'border-primary bg-primary/10 font-medium text-primary'
                : 'border-border text-muted-foreground hover:bg-muted',
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {receivables.isError ? (
        <p className="text-sm text-destructive">{toErrorMessage(receivables.error)}</p>
      ) : null}

      {receivables.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner className="h-6 w-6 text-muted-foreground" />
        </div>
      ) : items.length > 0 ? (
        <div className="grid gap-3 md:grid-cols-2">
          {items.map((item) => (
            <Card key={item.id} className={cn(item.isOverdue && 'border-destructive/40')}>
              <CardContent className="flex items-start gap-3 p-4">
                <Avatar name={item.customerName ?? 'Cliente'} image={item.customerImage} />

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate font-medium">{item.customerName ?? 'Cliente'}</p>
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-xs font-medium',
                        METHOD_TONE[item.method],
                      )}
                    >
                      {PAYMENT_METHOD_LABELS[item.method]}
                      {item.installments > 1 ? ` ${item.installment}/${item.installments}` : ''}
                    </span>
                    {item.isOverdue ? <Badge variant="destructive">Atrasado</Badge> : null}
                  </div>

                  <p className="truncate text-xs text-muted-foreground">
                    {item.machineName ? `${item.machineName} - ` : ''}
                    cai em {formatDate(item.dueDate)}
                    {item.professionalName ? ` - ${item.professionalName}` : ''}
                  </p>
                </div>

                <div className="shrink-0 text-right">
                  <p className="font-semibold tabular-nums">{formatCurrency(item.netAmount)}</p>
                  {item.feeAmount > 0 ? (
                    <p className="text-xs text-muted-foreground">
                      bruto {formatCurrency(item.grossAmount)}
                    </p>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card variant="flush">
          <CardContent className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              {tab === 'overdue'
                ? 'Nenhuma parcela atrasada.'
                : `Nada previsto para ${TABS.find((entry) => entry.key === tab)?.label.toLowerCase()}.`}
            </p>
          </CardContent>
        </Card>
      )}

      <Pagination
        meta={receivables.data?.meta}
        label="recebimentos"
        onChange={setPage}
        perPage={perPage}
        onPerPageChange={(value) => {
          setPerPage(value);
          setPage(1);
        }}
      />
    </div>
  );
}
